import React from 'react';

export interface CreateProviderOptions<ContextType> {
	/**
	 * If `true`, React will throw if context is `null`
	 */
	strict?: boolean;
	/**
	 * Error message to throw if the context is `null`
	 */
	errorMessage?: string;
	/**
	 * The display name of the context
	 */
	contextName?: string;
	/**
	 * The display name of the context
	 */
	defaultValue?: ContextType;
}

type CreateProviderReturn<T> = [React.Provider<T>, () => T, React.Context<T>];

/**
 * Creates a named context, provider, and hook.
 *
 * @param options create provider options
 */
export const createProvider = <ContextType>(options: CreateProviderOptions<ContextType> = {}) => {
	const {
		strict = true,
		errorMessage = 'Seems you forgot to wrap component within the Provider',
		contextName,
		defaultValue,
	} = options;

	const Context = React.createContext<ContextType | null>(defaultValue || null);

	Context.displayName = contextName;

	const useProvider = () => {
		const context = React.useContext(Context);

		if (strict && context === null) {
			const error = new Error(errorMessage);
			error.name = 'ProviderError';
			Error.captureStackTrace?.(error, useProvider);
			throw error;
		}

		return context;
	};

	return [Context.Provider, useProvider, Context] as CreateProviderReturn<ContextType>;
};
